import * as React from "react"
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Popover,
  Typography,
  Divider,
  Stack,
} from "@mui/material"
import { IoMdHelp } from "react-icons/io"
import { HiPlay, HiX } from "react-icons/hi"
import { useRouter } from "next/router"
import Link from "next/link"

import { helpData } from "data/help"
import { toursData, TourIds } from "data/tours"
import { useRegisterTours } from "context/TourProvider"
import { useBroadcastTour } from "context/BroadcastTourProvider"

function doesTourNameHaveTour(tourName?: string): tourName is TourIds {
  if (!tourName) return false
  return tourName in toursData
}

export default function HelpMenu({ children }: { children: React.ReactNode }) {
  useRegisterTours()
  const { pathname } = useRouter()
  const { broadcastTour } = useBroadcastTour()
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const open = Boolean(anchorEl)
  const helpContent = helpData?.[pathname]?.helpContent
  const loomEmbedUrl = helpData?.[pathname]?.loomEmbedUrl
  const tourName = helpData?.[pathname]?.tourName
  let tourTitle = ""
  if (doesTourNameHaveTour(tourName)) {
    tourTitle = toursData[tourName].title
  }

  return (
    <>
      <Tooltip title="Help and Resources" arrow placement="top-start">
        <Button
          onClick={handleClick}
          size="large"
          sx={{
            color: "primary.200",
            display: { xs: "none", sm: "flex" },
            backgroundColor: "gray.800",
            "&:hover, &:focus, &:active": {
              backgroundColor: "gray.700",
            },
            position: "fixed",
            bottom: 0,
            right: 0,
            margin: 3,
            px: 0,
            boxShadow: 2,
            borderRadius: 99,
            minWidth: "inherit",
            width: 36,
            height: 36,
          }}
        >
          <IoMdHelp />
        </Button>
      </Tooltip>
      <Box></Box>
      <Popover
        id="help-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        container={() => document.body}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        sx={{
          ".MuiPaper-root": {
            boxShadow: 2,
          },
        }}
      >
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            alignItems: "flex-start",
            textAlign: "left",
            padding: 4,
            minWidth: 300,
            maxWidth: 440,
            gap: 2,
            position: "relative",
            width: "100%",
          }}
        >
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: "12px",
              right: "12px",
            }}
          >
            <HiX />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="h3" fontSize="xl">
              Help
            </Typography>
            {doesTourNameHaveTour(tourName) && (
              <Button
                startIcon={<HiPlay />}
                variant="soft"
                onClick={() => {
                  handleClose()
                  broadcastTour(tourName)
                }}
              >
                {tourTitle}
              </Button>
            )}
          </Box>
          {helpContent && <Stack spacing={0.5}>{helpContent}</Stack>}
          {loomEmbedUrl && <LoomEmbed url={loomEmbedUrl} />}
          <Box
            sx={{
              color: "gray.600",
              fontSize: "sm",
            }}
          >
            Running into some issues? Email us at{" "}
            <Box
              component="a"
              sx={{ color: "primary.600" }}
              href="mailto:support@tryluz.com"
            >
              support@tryluz.com
            </Box>{" "}
            or check out our helpful docs below.
          </Box>
          <Divider
            light
            sx={{
              width: "100%",
            }}
          />
          <Box>
            <Typography variant="h4" fontSize="md">
              Helpful links
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyle: "none",
                padding: 0,
                margin: 0,
              }}
            >
              <Box component="li" sx={{ fontSize: "sm" }}>
                <Box
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "primary.600" }}
                  href="https://luzdata.crunch.help/"
                >
                  Luz University
                </Box>
              </Box>
              <Box component="li" sx={{ fontSize: "sm" }}>
                <Link passHref href="/dashboard/learn">
                  <Box component="a" sx={{ color: "primary.600" }}>
                    More product tours
                  </Box>
                </Link>
              </Box>
              <Box component="li" sx={{ fontSize: "sm" }}>
                <Box
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "primary.600" }}
                  href="https://www.tryluz.com/demo"
                >
                  Contact Sales
                </Box>
              </Box>
              <Box component="li" sx={{ fontSize: "sm" }}>
                <Box
                  component="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "primary.600" }}
                  href="mailto:support@tryluz.com"
                >
                  Support
                </Box>
              </Box>
            </Box>
          </Box>
          {children}
        </Box>
      </Popover>
    </>
  )
}

function LoomEmbed({ url }: { url: string }) {
  return (
    // @ts-ignore
    <Box
      component="iframe"
      src={url}
      allowFullScreen
      webkitAllowFullScreen
      mozAllowFullScreen
      sx={{
        width: "100%",
        height: "100%",
        aspectRatio: "16 / 9",
        borderWidth: 1,
        borderRadius: 1,
        borderStyle: "solid",
        borderColor: "gray.200",
        mb: 0,
        "& iframe": {
          width: "100%",
        },
      }}
    />
  )
}
